import { BadgeCheck, LayoutDashboard } from 'lucide-react';
import {
  CERTIFICATION_STAGE_LABELS,
  CERTIFICATION_STAGE_SCREENS,
  COMPANY_PREREQUISITE_LABEL,
} from '@/features/onboarding/certification-stage';
import { CERTIFICATION_STAGES } from '@/features/certification/avance/api';
import type { NavChild, NavItem } from './types';

/**
 * The company prerequisite, then the SII's steps that have a screen of their
 * own, in the order they are done. The SII's last step, «Autorizado», is the
 * SII's alone and has nothing to link to.
 */
const STEPS: readonly NavChild[] = CERTIFICATION_STAGES.flatMap((stage) => {
  const to = CERTIFICATION_STAGE_SCREENS[stage];
  return to === null ? [] : [{ label: CERTIFICATION_STAGE_LABELS[stage], to }];
});

/**
 * The «Certificación» destination, with the status and then every step of the
 * certification as a second level.
 *
 * It had been collapsed to one entry (the status screen's stepper was meant to
 * be the way into each step), and people missed seeing the steps in the menu:
 * where they are, in which order, and which ones are not open yet. The steps
 * are back, in the same order as the stepper, and the ones the SII has not
 * reached are shown locked with what unlocks them (`use-certification-locks.ts`)
 * instead of being hidden.
 *
 * `to` is the status, as the first child, like «Folios» does with its stock.
 */
export const certificationNav: readonly NavItem[] = [
  {
    label: 'Certificación',
    to: '/certification/status',
    matchPrefix: '/certification',
    icon: BadgeCheck,
    group: 'setup',
    // The setup before the SII is done by whoever administers the organisation, not by whoever issues every day.
    minimumPermission: 'settings:write',
    children: [
      // The overview of every step below, not a step itself: the shell lifts it
      // out of the steps' line and gives it an icon.
      {
        label: 'Estado de certificación',
        to: '/certification/status',
        overview: { icon: LayoutDashboard },
      },
      { label: COMPANY_PREREQUISITE_LABEL, to: '/certification/onboarding' },
      ...STEPS,
    ],
  },
];
